import React, { useState } from 'react';
import ReactModal from 'react-modal';
import { Button, Progress, Upload, UploadProps, message } from 'antd';
import {
  CloseCircleOutlined,
  DeleteOutlined,
  InboxOutlined,
  ReloadOutlined,
  SendOutlined,
} from '@ant-design/icons';
import {
  JOB_FIT_ALIGNMENT_CLASS,
  formatAlignmentLabel,
  visibleSkills,
} from './jobFit.shared';
import JobFitDisclaimer from './JobFitDisclaimer';
import type { JobFitFlow } from './useJobFitFlow';
import KofiSupportPanel from '../../../support/KofiSupportPanel';
import ToolRunAllowance from '../../../support/ToolRunAllowance';
import PublicConsentCheckbox from '../../../components/PublicConsentCheckbox';

type JobFitModalProps = {
  jobTitle: string;
  company?: string;
  featured: boolean;
  flow: JobFitFlow;
  onEasyApply?: () => void;
};

const MATCHED_SKILLS_LIMIT = 8;
const MISSING_SKILLS_LIMIT = 6;

const JobFitModal: React.FC<JobFitModalProps> = ({
  jobTitle,
  company,
  featured,
  flow,
  onEasyApply,
}) => {
  const [hasConsented, setHasConsented] = useState(false);
  const {
    phase,
    modalOpen,
    selectedFile,
    progressMessage,
    progressPercent,
    fitResult,
    errorMessage,
    gateStatus,
    kofiRequired,
    requestCloseModal,
    selectFile,
    analyzeSelectedFile,
    handleRedeemed,
    checkAgain,
  } = flow;

  const uploadProps: UploadProps = {
    multiple: false,
    maxCount: 1,
    accept: '.pdf,.docx',
    showUploadList: false,
    disabled: phase === 'working',
    beforeUpload(file) {
      const isPdf = file.type === 'application/pdf';
      const isDocx =
        file.type ===
        'application/vnd.openxmlformats-officedocument.wordprocessingml.document';
      if (!isPdf && !isDocx) {
        message.error('Only PDF or DOCX files are allowed.');
        return Upload.LIST_IGNORE;
      }
      selectFile(file);
      return false;
    },
  };

  const matched = fitResult
    ? visibleSkills(fitResult.matched_skills, MATCHED_SKILLS_LIMIT)
    : { visible: [], remaining: 0 };
  const missing = fitResult
    ? visibleSkills(fitResult.missing_skills, MISSING_SKILLS_LIMIT)
    : { visible: [], remaining: 0 };

  const handleRequestClose = () => {
    setHasConsented(false);
    requestCloseModal();
  };

  const handleEasyApply = () => {
    handleRequestClose();
    onEasyApply?.();
  };

  const handleCheckAgain = () => {
    setHasConsented(false);
    checkAgain();
  };

  return (
    <ReactModal
      isOpen={modalOpen}
      onRequestClose={handleRequestClose}
      className="job-fit-modal"
      overlayClassName="fixed inset-0 z-[1000] flex items-center justify-center bg-black/50 p-3 sm:p-4"
      ariaHideApp={false}
      contentLabel={`Check your fit for ${jobTitle}`}
    >
      <div className="job-fit-modal__scroll">
        <div className="public-panel job-fit-modal__panel">
          <div className="job-fit-modal__header">
            <div>
              <p className="public-eyebrow mb-1">Resume fit</p>
              <h2 className="jo-heading m-0 text-xl font-black sm:text-2xl">
                Check your fit
              </h2>
              <p className="jo-body-subtle mb-0 mt-2 text-sm">
                {company ? `${company} · ` : ''}
                {jobTitle}
              </p>
            </div>
            <button
              type="button"
              className="job-fit-modal__close"
              onClick={handleRequestClose}
              aria-label="Close fit check"
            >
              <CloseCircleOutlined />
            </button>
          </div>

          <div className="public-panel-body ats-stack job-fit-modal__body">
            <ToolRunAllowance status={gateStatus} />
            {(phase === 'idle' || phase === 'failed') && (
              <>
                <Upload.Dragger
                  {...uploadProps}
                  className="job-fit-upload job-fit-upload--modal"
                >
                  <p className="ant-upload-drag-icon mb-2">
                    <InboxOutlined />
                  </p>
                  <p className="ant-upload-text mb-1 text-sm">
                    Upload PDF or DOCX
                  </p>
                  <p className="ant-upload-hint mb-0 text-xs">
                    Drag a file here or click to browse
                  </p>
                </Upload.Dragger>

                {selectedFile && (
                  <div className="job-fit-selected-file">
                    <span className="job-fit-selected-file__name">
                      {selectedFile.name}
                    </span>
                    <Button
                      type="text"
                      icon={<DeleteOutlined />}
                      aria-label="Remove selected file"
                      onClick={() => selectFile(null)}
                    />
                  </div>
                )}

                <PublicConsentCheckbox
                  id="job-fit-consent"
                  checked={hasConsented}
                  purpose="the processing of my resume to assess fit for this job"
                  onChange={setHasConsented}
                />
                <JobFitDisclaimer className="job-fit-legal--modal" />

                {phase === 'failed' && (
                  <p className="job-fit-error mb-0" role="alert">
                    {errorMessage ?? 'Something went wrong. Please try again.'}
                  </p>
                )}

                <Button
                  type="primary"
                  className="public-button-primary w-full"
                  onClick={analyzeSelectedFile}
                  disabled={!selectedFile || !hasConsented}
                >
                  Analyze resume
                </Button>
              </>
            )}

            {phase === 'payment' && kofiRequired && (
              <KofiSupportPanel
                required={kofiRequired}
                onRedeemed={handleRedeemed}
              />
            )}

            {phase === 'working' && (
              <div
                className="job-fit-progress job-fit-progress--modal"
                aria-live="polite"
              >
                <Progress
                  percent={progressPercent}
                  status="active"
                  strokeColor="var(--jo-primary)"
                />
                <p className="jo-body-subtle mb-0 text-center text-sm">
                  {progressMessage}
                </p>
              </div>
            )}

            {phase === 'completed' && fitResult && (
              <div className="job-fit-result job-fit-result--modal ats-stack">
                <div
                  className={`job-fit-score ${JOB_FIT_ALIGNMENT_CLASS[fitResult.alignment]}`}
                  aria-label={`Fit score ${fitResult.score} out of 100`}
                >
                  <span className="job-fit-score__value">
                    {fitResult.score}
                  </span>
                  <span className="job-fit-score__label">
                    {formatAlignmentLabel(fitResult.alignment)}
                  </span>
                </div>

                {fitResult.skills_source === 'INFERRED' && (
                  <p className="job-fit-inferred-note mb-0">
                    Skills inferred from the job description.
                  </p>
                )}

                <div className="job-fit-skills-grid">
                  {matched.visible.length > 0 && (
                    <div>
                      <p className="job-fit-section-label">Matched skills</p>
                      <div className="job-fit-chip-row">
                        {matched.visible.map((skill) => (
                          <span
                            key={skill}
                            className="job-fit-chip job-fit-chip--matched"
                          >
                            {skill}
                          </span>
                        ))}
                        {matched.remaining > 0 && (
                          <span className="job-fit-chip job-fit-chip--more">
                            +{matched.remaining} more
                          </span>
                        )}
                      </div>
                    </div>
                  )}

                  {missing.visible.length > 0 && (
                    <div>
                      <p className="job-fit-section-label">Gaps</p>
                      <div className="job-fit-chip-row">
                        {missing.visible.map((skill) => (
                          <span
                            key={skill}
                            className="job-fit-chip job-fit-chip--missing"
                          >
                            {skill}
                          </span>
                        ))}
                        {missing.remaining > 0 && (
                          <span className="job-fit-chip job-fit-chip--more">
                            +{missing.remaining} more
                          </span>
                        )}
                      </div>
                    </div>
                  )}
                </div>

                <p className="job-fit-summary mb-0">{fitResult.summary}</p>
                <JobFitDisclaimer />

                <div className="job-fit-modal__actions">
                  {featured && onEasyApply && (
                    <Button
                      type="primary"
                      className="public-button-primary"
                      icon={<SendOutlined />}
                      onClick={handleEasyApply}
                    >
                      Easy Apply
                    </Button>
                  )}
                  <Button
                    type="default"
                    className="public-button-secondary"
                    icon={<ReloadOutlined />}
                    onClick={handleCheckAgain}
                  >
                    Check again
                  </Button>
                  <Button
                    type="default"
                    className="public-button-secondary"
                    onClick={handleRequestClose}
                  >
                    Close
                  </Button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </ReactModal>
  );
};

export default JobFitModal;
